import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApi } from '../services/apiServices';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

function Footer({ className = '', openDeviceModal }) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [footerCountries, setFooterCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [userChangedCurrency, setUserChangedCurrency] = useState(false);

  // Language mapping function to convert app language codes to API language codes
  const getApiLanguageCode = (appLanguage) => {
    const languageMap = {
      'ar': 'ar',     // Arabic
      'da': 'da',     // Danish
      'de': 'de',     // German
      'en': 'en',     // English
      'es': 'es',     // Spanish
      'fr': 'fr',     // French
      'he': 'he',     // Hebrew
      'it': 'it',     // Italian
      'ja': 'ja',     // Japanese
      'ko': 'ko',     // Korean
      'nl': 'nl',     // Dutch
      'no': 'no',     // Norwegian
      'pl': 'pl',     // Polish
      'pt': 'pt',     // Portuguese
      'ru': 'ru',     // Russian
      'sv': 'sv',     // Swedish
      'tr': 'tr',     // Turkish
      'zh': 'zh_CN'   // Chinese (Simplified)
    };
    return languageMap[appLanguage] || 'en'; // Default to English if not found
  };

  // 🍪 Cookie Consent state
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);
  const [cookiePrefs, setCookiePrefs] = useState({
    essential: true, // always enabled
    performance: false,
    functionality: false,
    marketing: false
  });

  // Load stored currency
  useEffect(() => {
    const storedCurrency = localStorage.getItem('selectedCurrency');
    if (storedCurrency) {
      setSelectedCurrency(storedCurrency);
    }
  }, []);

  // Watch for cart updates (currency change)
  useEffect(() => {
    const handleCurrencyChange = () => {
      const currency = localStorage.getItem('selectedCurrency') || 'USD';
      setSelectedCurrency(currency);
      setUserChangedCurrency(true);
    };
    window.addEventListener('cartUpdated', handleCurrencyChange);
    return () => window.removeEventListener('cartUpdated', handleCurrencyChange);
  }, []);

  // Fetch footer countries
  useEffect(() => {
    const fetchFooterCountries = async () => {
      try {
        setLoading(true);
        setError(null);
        const currentCurrency = localStorage.getItem('selectedCurrency') || 'USD';
        const apiLanguage = getApiLanguageCode(currentLanguage);
        const currencyParam = currentCurrency !== 'USD' ? `&currency=${currentCurrency}` : '';
        const response = await getApi(`/api/countries?count=6&page=1&filter=popular${currencyParam}&lang=${apiLanguage}`);

        if (response?.status && response?.data?.countries) {
          setFooterCountries(response.data.countries);
        } else {
          setFooterCountries([]);
          setError('Failed to fetch footer countries');
        }
      } catch (error) {
        console.error('Error fetching footer countries:', error);
        setError('Failed to fetch footer countries');
        setFooterCountries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFooterCountries();
  }, [selectedCurrency, userChangedCurrency, currentLanguage]);

  // 🍪 Handle cookie consent
  useEffect(() => {
    const savedPrefs = localStorage.getItem('cookiePrefs');
    if (!savedPrefs) {
      setShowCookieBanner(true);
    } else {
      setCookiePrefs(JSON.parse(savedPrefs));
    }
  }, []);

  const saveCookiePrefs = (prefs) => {
    localStorage.setItem('cookiePrefs', JSON.stringify(prefs));
    setCookiePrefs(prefs);
    setShowCookieBanner(false);
    setShowCookieModal(false);
  };

  const acceptAllCookies = () => {
    const allPrefs = {
      essential: true,
      performance: true,
      functionality: true,
      marketing: true
    };
    saveCookiePrefs(allPrefs);
  };

  return (
    <footer className={`r4l-footer ${className}`.trim()}>
      <div className="r4l-wrap r4l-foot-grid">
        {/* Brand */}
        <div className="r4l-foot-brand">
          <Link className="r4l-logo" to="/">
            <img src={window.getPublicUrl('images/logo.png')} alt="Roam4Less" />
          </Link>
          <p>{t('footer.description')}</p>
          <ul className="r4l-socials">
            <li><a href="https://www.instagram.com/roam4less.app" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><img src={window.getPublicUrl('images/footer-insta.svg')} alt="" /></a></li>
            {/* <li><a href="https://www.facebook.com/roam4lessAPP" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img src={window.getPublicUrl('images/footer-fb.svg')} alt="" /></a></li> */}
            <li><a href="https://x.com/roam4less_app" target="_blank" rel="noopener noreferrer" aria-label="X"><img src={window.getPublicUrl('images/x_black.png')} alt="" /></a></li>
            <li><a href="https://www.tiktok.com/@roam.4.less" target="_blank" rel="noopener noreferrer" aria-label="TikTok"><img src={window.getPublicUrl('images/tiktok Black.svg')} alt="" /></a></li>
          </ul>
        </div>

        {/* Top Destinations */}
        <div>
          <h3>{t('footer.destinations.title')}</h3>
          <ul>
            {loading ? (
              <li>{t('footer.destinations.loading')}</li>
            ) : error ? (
              <li>{t('footer.destinations.error')}</li>
            ) : footerCountries.length === 0 ? (
              <li>{t('footer.destinations.noData')}</li>
            ) : (
              footerCountries.map((country, index) => (
                <li key={index}>
                  <Link
                    to={`/destination-detail/${country.iso3}`}
                    state={{ countryData: country }}
                  >
                    {country.name}
                  </Link>
                </li>
              ))
            )}
          </ul>
        </div>

        {/* Legal */}
        <div>
          <h3>{t('footer.legal.title')}</h3>
          <ul>
            <li><Link to="/terms-and-conditions">{t('footer.legal.termsAndConditions')}</Link></li>
            <li><Link to="/privacy-policy">{t('footer.legal.privacyPolicy')}</Link></li>
            <li><Link to="/cookies-policy">{t('footer.legal.cookiesPolicy')}</Link></li>
            <li>
              <a href="#" onClick={(e) => { e.preventDefault(); setShowCookieModal(true); }}>
                {t('footer.legal.cookieSettings')}
              </a>
            </li>
            <li><Link to="/refund-policy">{t('footer.legal.refundPolicy')}</Link></li>
            <li><Link to="/terms-of-use">{t('footer.legal.websiteTermsOfUse')}</Link></li>
            <li><Link to="/contactUs">{t('header.nav.contactUs')}</Link></li>
          </ul>
        </div>

        {/* Interest */}
        <div>
          <h3>{t('footer.interest.title')}</h3>
          <ul>
            <li><Link to="/esim">{t('footer.interest.whatIsEsim')}</Link></li>
            <li><a href="#" onClick={e => { e.preventDefault(); openDeviceModal && openDeviceModal(); }}>{t('footer.interest.compatibleDevices')}</a></li>
            <li><Link to="/activateEsim">{t('footer.interest.installAndActivate')}</Link></li>
            <li><Link to="/data-calculator">{t('footer.interest.dataCalculator')}</Link></li>
            <li><Link to="/Faq">{t('footer.interest.faqs')}</Link></li>
            <li><Link to="/blog">{t('footer.interest.blog')}</Link></li>
          </ul>
        </div>

        {/* Partnerships + app */}
        <div>
          <h3>{t('footer.partnerships.title')}</h3>
          <ul>
            <li><Link to="/partnerships">{t('footer.partnerships.forBusiness')}</Link></li>
          </ul>
          <h3 style={{ marginTop: 24 }}>{t('appDownloadModal.title')}</h3>
          <ul className="r4l-stores">
            <li>
              <a href="https://apps.apple.com/in/app/roam4less-esim-travel-data/id6756699233" target="_blank" rel="noopener noreferrer">
                <img src={window.getPublicUrl('images/app-store.png')} alt={t('appDownloadModal.appStore')} />
              </a>
            </li>
            <li>
              <a href="https://play.google.com/store/apps/details?id=com.roam4less.app&hl=en_IN" target="_blank" rel="noopener noreferrer">
                <img src={window.getPublicUrl('images/google-play.png')} alt={t('appDownloadModal.googlePlay')} />
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Copyright */}
      <div className="r4l-wrap r4l-legal">
        <span>{t('footer.copyright')}</span>
        <a href="tel:+18594493673">+1 (859) 449-3673</a>
      </div>

      {/* 🍪 Cookie Banner */}
      {/* {showCookieBanner && (
        <div className="cookie-banner">
          <p>At Roam4Less, you control your privacy. Select which cookies you allow.</p>
          <button onClick={acceptAllCookies}>Accept All</button>
          <button onClick={() => { setShowCookieBanner(false); setShowCookieModal(true); }}>
            Customize
          </button>
        </div>
      )} */}

      {/* 🍪 Cookie Modal */}
    {showCookieModal && (
  <div className="cookie-modal">
    <div className="modal-content">
      <h3>{t('footer.cookieSettings.title')}</h3>
      <p>{t('footer.cookieSettings.description')}</p>

      {/* Essential Cookies (disabled) */}
      <label className="term disabled">
        <input type="checkbox" checked disabled />
        <span className="checkmark"></span>
        {t('footer.cookieSettings.essential')}
      </label>

      {/* Performance Cookies */}
      <label className="term">
        <input
          type="checkbox"
          checked={cookiePrefs.performance}
          onChange={(e) =>
            setCookiePrefs({ ...cookiePrefs, performance: e.target.checked })
          }
        />
        <span className="checkmark"></span>
        {t('footer.cookieSettings.performance')}
      </label>

      {/* Functionality Cookies */}
      <label className="term">
        <input
          type="checkbox"
          checked={cookiePrefs.functionality}
          onChange={(e) =>
            setCookiePrefs({ ...cookiePrefs, functionality: e.target.checked })
          }
        />
        <span className="checkmark"></span>
        {t('footer.cookieSettings.functionality')}
      </label>

      {/* Marketing Cookies */}
      <label className="term">
        <input
          type="checkbox"
          checked={cookiePrefs.marketing}
          onChange={(e) =>
            setCookiePrefs({ ...cookiePrefs, marketing: e.target.checked })
          }
        />
        <span className="checkmark"></span>
        {t('footer.cookieSettings.marketing')}
      </label>

      {/* Modal Actions */}
      <div className="modal-actions">
        <button onClick={() => saveCookiePrefs(cookiePrefs)}>
          {t('footer.cookieSettings.savePreferences')}
        </button>
        <button onClick={acceptAllCookies}>
          {t('footer.cookieSettings.acceptAll')}
        </button>
        <button onClick={() => setShowCookieModal(false)}>
          {t('footer.cookieSettings.cancel')}
        </button>
      </div>
    </div>
  </div>
)}

    </footer>
  );
}

export default Footer;
